import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { CollectionInfo } from "@redotech/shopify-storefront";
import { isDef } from "@redotech/util/checks";
import { memo, useMemo, useRef, useState } from "react";
import { useDebounce } from "usehooks-ts";

export const CollectionSelectionModal = memo(function CollectionSelectionModal({
  selectedCollections,
  onCollectionsSelected,
  onClose,
  searchCollections,
}: {
  selectedCollections: string[];
  onCollectionsSelected: (collections: string[]) => void;
  onClose: () => void;
  searchCollections: (params: { search: string }) => Promise<CollectionInfo[]>;
}) {
  const [localSelectedCollections, setLocalSelectedCollections] =
    useState<string[]>(selectedCollections);
  const [searchString, setSearchString] = useState("");

  const debouncedSearch = useDebounce(searchString, 500);

  const collectionsLoad = useLoad(
    async () => await searchCollections({ search: debouncedSearch }),
    [debouncedSearch, searchCollections],
  );

  // Cache of all seen collections so selected items retain their title even
  // when they're no longer in the current search results.
  const knownCollectionsRef = useRef<Map<string, string>>(new Map());

  const collectionOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    for (const collection of collectionsLoad.value ?? []) {
      if (collection) {
        knownCollectionsRef.current.set(collection.id, collection.title);
      }
    }
    const loadedIds = new Set(
      collectionsLoad.value?.filter(isDef).map((c) => c.id) ?? [],
    );
    const searchResults: BadgeRedoListItem<string>[] =
      collectionsLoad.value
        ?.filter(isDef)
        .map((collection) => ({
          type: "badge",
          id: collection.id,
          value: collection.id,
          badge: { text: collection.title, color: "gray" },
        })) ?? [];

    // Include selected collections that aren't in the current search results
    // so they remain visible and checked in the dropdown.
    const selectedNotInResults = localSelectedCollections
      .filter((id) => !loadedIds.has(id))
      .map<
        BadgeRedoListItem<string>
      >((id) => ({ type: "badge", id, value: id, badge: { text: knownCollectionsRef.current.get(id) ?? id, color: "gray" } }));

    return [...searchResults, ...selectedNotInResults];
  }, [collectionsLoad.value, localSelectedCollections]);

  const selectedOptions = useMemo(() => {
    return collectionOptions.filter((option) =>
      localSelectedCollections.includes(option.value),
    );
  }, [collectionOptions, localSelectedCollections]);

  const handleSelectionChange = useHandler(
    (items: BadgeRedoListItem<string>[]) => {
      setLocalSelectedCollections(items.map((item) => item.value));
    },
  );

  const handleSave = useHandler(() => {
    onCollectionsSelected(localSelectedCollections);
  });

  return (
    <RedoModal
      modalSize="md"
      onModalCloseRequested={onClose}
      primaryButton={{ text: "Save", onClick: handleSave }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      title="Select Collections"
    >
      <Flex dir="column" gap="lg" p="md">
        <RedoTagsDropdownInput<string>
          label="Collections"
          loading={collectionsLoad.pending}
          options={collectionOptions}
          placeholder="Select collections"
          searchPlaceholder="Search for collections"
          searchString={searchString}
          selectedOptions={selectedOptions}
          setSearchString={setSearchString}
          setSelectedOptions={handleSelectionChange}
          size="xs"
        />
      </Flex>
    </RedoModal>
  );
});
